import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { PermPostOfferWorkflowModalManagerService } from 'src/app/accountmanagers/perm-post-offer/services/perm-post-offer-workflow-modal-manager.service';
import { PermPostOfferService } from 'src/app/accountmanagers/perm-post-offer/services/perm-post-offer.service';
import { AmSubmittalStatusUpdateService } from 'src/app/accountmanagers/submittals/am-submittal-status-update/am-submittal-status-update.service';
import { PermOfferModalComponent } from 'src/app/accountmanagers/submittals/am-submittal-status-update/perm-offer-modal/perm-offer-modal.component';
import * as PermPostOfferWorkflowActions from '../actions/index';
import { PermPostOfferModalActionPayload } from '../models/perm-post-offer-modal-action-payload.model';
import { TypedAction } from '@ngrx/store/src/models';
import { PermPostOfferOfferModel } from '../models/perm-post-offer-offer.model';
import { Store } from '@ngrx/store';
import { PostOfferModalLoadPayload } from '../models/perm-post-offer-modal-load-payload.model';
import { PermPostOfferWorkflowSubmittalInformationExpanded } from '../models/perm-post-offer-workflow-submittal-info.model';
import { AmSubmittalsService } from 'src/app/accountmanagers/submittals/am-submittals.service';
import { SubmittalRemovalReason } from 'src/app/accountmanagers/submittals/model/submittal-removal-reasons.model';
import { AccountManagersService } from 'src/app/accountmanagers/accountmanagers.service';
import { HttpResponse } from '@angular/common/http';
import { AccountManagersJobDetailsDto } from 'src/app/shared/models/account-managers-job-details.dto';
import { PostOfferInformationSerializable } from '../models/perm-post-offer-information.model';
import { SetPermanentJobSubmittalToOfferResultDto } from 'src/app/accountmanagers/perm-post-offer/models/set-permanent-job-submittal-to-offer-result-dto.model';
import { PermPostOfferUpdateOfferPayload } from '../models/perm-post-offer-update-offer-payload.model';
import { ClosePostOfferModalPayload } from '../models/close-post-offer-modal-payload.model';
import { SubmittalJobStatus } from 'src/app/shared/models/enums/submittal-job-status.enum';
import { AMNoteItem } from 'src/app/accountmanagers/submittals/model/am-submittals.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { PermanentPlacementNote } from '../models/perm-placement-note.model';
import { PostOfferModalMetadata } from '../models/post-offer-modal-metadata.model';
import { SubmittalTypeEnum } from 'src/app/accountmanagers/submittals/model/am-submittals.enum';
import * as permPostOfferCandidateWorkflowSelectors from '../perm-post-offer-candidate-workflow.selectors';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';

export const candidateDeclinedRemovalReason = 35;

@Injectable()
export class PermPostOfferModalEffects {
    openPermOfferModal$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                PermPostOfferWorkflowActions.openPermOfferModal,
                PermPostOfferWorkflowActions.openPermOfferModalFromGrid
            ),
            switchMap((payload) => {
                return this.createPostOfferModal(payload, true);
            })
        );
    });

    openPermOfferModalFromGrid$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                PermPostOfferWorkflowActions.openPlacedPermOfferModal,
                PermPostOfferWorkflowActions.openLegacyPermOfferModal
            ),
            switchMap((payload) => {
                return this.createPostOfferModal(payload, false);
            })
        );
    });

    fetchPermBillingInformation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferWorkflowActions.loadPostOfferModal),
            concatLatestFrom(() => [
                this.store.select(permPostOfferCandidateWorkflowSelectors.selectSubmittalInformation)
            ]),
            switchMap(
                ([_, candidateSubmittalInformation]: [
                    PostOfferModalLoadPayload,
                    PermPostOfferWorkflowSubmittalInformationExpanded
                ]) => {
                    return this._permPostOfferService
                        .getPermPlacementNotes(candidateSubmittalInformation.facilityId)
                        .pipe(
                            map((permBillingInformation: PermanentPlacementNote) =>
                                PermPostOfferWorkflowActions.fetchPermBillingInformationSuccess({
                                    facilityBillingInformation: permBillingInformation.note
                                })
                            ),
                            catchError((_) => {
                                this.showGenericErrorMessage();
                                return of(PermPostOfferWorkflowActions.fetchPermBillingInformationFailure());
                            })
                        );
                }
            )
        );
    });

    fetchOfferDeclinedRemovalReasons$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferWorkflowActions.loadPostOfferModal),
            switchMap((actionPayload: PostOfferModalLoadPayload) => {
                if (actionPayload.shouldBypassFetchingRemovalReasons) {
                    return of(
                        PermPostOfferWorkflowActions.fetchPermOfferDeclinedRemovalReasonsSuccess({ removalReasons: [] })
                    );
                }

                return this._amSubmittalsService.getSubmittalRemovalReasons(candidateDeclinedRemovalReason).pipe(
                    map((removalReasons: SubmittalRemovalReason[]) =>
                        PermPostOfferWorkflowActions.fetchPermOfferDeclinedRemovalReasonsSuccess({
                            removalReasons: removalReasons
                        })
                    ),
                    catchError((_) => {
                        this.showGenericErrorMessage();
                        return of(PermPostOfferWorkflowActions.fetchPermOfferDeclinedRemovalReasonsFailure());
                    })
                );
            })
        );
    });

    fetchJobBillRate$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferWorkflowActions.loadPostOfferModal),
            concatLatestFrom(() => [
                this.store.select(permPostOfferCandidateWorkflowSelectors.selectSubmittalInformation)
            ]),
            switchMap(
                ([_, candidateSubmittalInformation]: [
                    PostOfferModalLoadPayload,
                    PermPostOfferWorkflowSubmittalInformationExpanded
                ]) => {
                    return this._accountManagersService.getJobDetails(candidateSubmittalInformation.jobId).pipe(
                        map((apiResponse: HttpResponse<any>) => {
                            if (!apiResponse.ok) {
                                this.showGenericErrorMessage();
                                return PermPostOfferWorkflowActions.fetchJobBillRateFailure();
                            }

                            const jobDetails = apiResponse.body as AccountManagersJobDetailsDto;

                            return PermPostOfferWorkflowActions.fetchJobBillRateSuccess({
                                billRate: jobDetails.billRate,
                                placementFee: jobDetails.placementFee
                            });
                        }),
                        catchError((_) => {
                            this.showGenericErrorMessage();
                            return of(PermPostOfferWorkflowActions.fetchJobBillRateFailure());
                        })
                    );
                }
            )
        );
    });

    fetchPostOfferData$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferWorkflowActions.loadPostOfferModal),
            concatLatestFrom(() => [
                this.store.select(permPostOfferCandidateWorkflowSelectors.selectSubmittalInformation)
            ]),
            switchMap(
                ([_, candidateSubmittalInformation]: [
                    PostOfferModalLoadPayload,
                    PermPostOfferWorkflowSubmittalInformationExpanded
                ]) => {
                    return this._permPostOfferService
                        .getOfferInformation(
                            candidateSubmittalInformation.uniqueCandidateAndJobApplicationId,
                            candidateSubmittalInformation.submittalType
                        )
                        .pipe(
                            map((postOfferInformation: PostOfferInformationSerializable) =>
                                PermPostOfferWorkflowActions.fetchPermPostOfferDataSuccess(postOfferInformation)
                            ),
                            catchError((_) => {
                                this.showGenericErrorMessage();
                                return of(PermPostOfferWorkflowActions.fetchPermPostOfferDataFailure());
                            })
                        );
                }
            )
        );
    });

    updatePostOfferData$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferWorkflowActions.updateOfferInfoAndSubmittalStatus),
            concatLatestFrom(() => [
                this.store.select(permPostOfferCandidateWorkflowSelectors.selectSubmittalInformation),
                this.store.select(permPostOfferCandidateWorkflowSelectors.selectLegacyStatusId),
                this.store.select(permPostOfferCandidateWorkflowSelectors.selectPostOfferModelMetadata)
            ]),
            switchMap(
                ([
                    actionPayload,
                    candidateSubmittalInformation,
                    candidateLegacyStatusId,
                    candidatePostOfferModalMetadata
                ]: [
                    PermPostOfferUpdateOfferPayload,
                    PermPostOfferWorkflowSubmittalInformationExpanded,
                    SubmittalJobStatus | undefined,
                    PostOfferModalMetadata | undefined
                ]) => {
                    return this._permPostOfferService
                        .updateOfferInformation(
                            candidateSubmittalInformation,
                            actionPayload,
                            candidateLegacyStatusId,
                            candidatePostOfferModalMetadata
                        )
                        .pipe(
                            map((postOfferUpdateResult: SetPermanentJobSubmittalToOfferResultDto) => {
                                this._toasterService.toast('Submittal has been successfully processed.');

                                return PermPostOfferWorkflowActions.closePostOfferModal({
                                    updateResult: postOfferUpdateResult
                                });
                            }),
                            catchError((_) => {
                                this.showGenericErrorMessage();

                                return of(PermPostOfferWorkflowActions.updateOfferInfoAndSubmittalStatusFailure());
                            })
                        );
                }
            )
        );
    });

    handleFetchFailures$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(
                    PermPostOfferWorkflowActions.fetchJobBillRateFailure,
                    PermPostOfferWorkflowActions.fetchPermBillingInformationFailure,
                    PermPostOfferWorkflowActions.fetchPermOfferDeclinedRemovalReasonsFailure,
                    PermPostOfferWorkflowActions.fetchPermPostOfferDataFailure
                ),
                map(() => {
                    this._permPostOfferWorkflowModalManagerService.closeModal();
                })
            );
        },
        { dispatch: false }
    );

    cancelPostOfferModal$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferWorkflowActions.cancelPostOfferModal),
            concatLatestFrom(() => [
                this.store.select(permPostOfferCandidateWorkflowSelectors.selectLegacyStatusId),
                this.store.select(permPostOfferCandidateWorkflowSelectors.selectSubmittalInformation)
            ]),
            switchMap(
                ([_, candidateLegacyStatusId, candidateSubmittalInformation]: [
                    TypedAction<'[Perm Offer Modal Component] Cancel Post Offer Modal'>,
                    SubmittalJobStatus | undefined,
                    PermPostOfferWorkflowSubmittalInformationExpanded
                ]) => {
                    return this.runCancelProcesses();
                }
            )
        );
    });

    closePostOfferModal$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferWorkflowActions.closePostOfferModal),
            concatLatestFrom(() => [
                this.store.select(permPostOfferCandidateWorkflowSelectors.selectSubmittalInformation)
            ]),
            switchMap(
                ([payload, candidateSubmittalInformation]: [
                    ClosePostOfferModalPayload,
                    PermPostOfferWorkflowSubmittalInformationExpanded
                ]) => {
                    return this.runPostUpdateProcesses(candidateSubmittalInformation, payload);
                }
            )
        );
    });

    private showGenericErrorMessage(): void {
        this._toasterService.toast('An error occurred, please try again.', 5000);
    }

    private runPostUpdateProcesses(
        submittalInformation: PermPostOfferWorkflowSubmittalInformationExpanded,
        closeModalPayload: ClosePostOfferModalPayload
    ): Observable<TypedAction<'[Perm Offer Effect] Clear Post Offer State'>> {
        const submittalRemovalOccurred = closeModalPayload.updateResult.removedFromList;

        this._statusUpdateService.notifySubmittalJobUpdated(
            closeModalPayload.updateResult.submittalJob,
            submittalRemovalOccurred,
            false
        );

        const shouldPerformWholeGridRefresh: boolean =
            submittalRemovalOccurred && submittalInformation.submittalType === SubmittalTypeEnum.APN;
        // APN submittals are grouped differently in the UI than AYA. If they are removed, then the whole grid will need to be refreshed
        // so the overarching submittal can be removed from the grid.
        // This refreshes the overarching submittal information with an optional whole grid refresh
        this._statusUpdateService.triggerSubmittalStatusChanged(
            closeModalPayload.updateResult.submittalJob.submittalId,
            shouldPerformWholeGridRefresh
        );

        const note: AMNoteItem | undefined = closeModalPayload.updateResult.submittalNote;
        if (note) {
            this._statusUpdateService.notifyNoteUpdated(note);
        }

        return this.closeModalAndCleanupState();
    }

    private runCancelProcesses(): Observable<TypedAction<'[Perm Offer Effect] Clear Post Offer State'>> {
        return this.closeModalAndCleanupState();
    }

    private closeModalAndCleanupState(): Observable<TypedAction<'[Perm Offer Effect] Clear Post Offer State'>> {
        this._permPostOfferWorkflowModalManagerService.closeModal();

        return of(PermPostOfferWorkflowActions.clearPostOfferState());
    }

    private createPostOfferModal(
        actionPayload: PermPostOfferModalActionPayload,
        shouldDisplayOfferStatuses: boolean
    ): Observable<TypedAction<'[Perm Offer Effect] Load Post Offer Modal'>> {
        const payload: PermPostOfferOfferModel = {
            facilityLink: actionPayload.headerLinkInformation.facilityLink,
            facilityName: actionPayload.headerLinkInformation.facilityName,
            displayOfferStatusSelection: shouldDisplayOfferStatuses,
            jobLink: actionPayload.headerLinkInformation.jobLink,
            jobName: actionPayload.headerLinkInformation.jobName,
            candidateLink: actionPayload.headerLinkInformation.candidateLink,
            candidateName: actionPayload.headerLinkInformation.candidateName
        };

        this._permPostOfferWorkflowModalManagerService.createModal(PermOfferModalComponent, payload, '700px', '700px');

        return of(
            PermPostOfferWorkflowActions.loadPostOfferModal({
                shouldBypassFetchingRemovalReasons: !shouldDisplayOfferStatuses
            })
        );
    }

    constructor(
        private readonly _actions$: Actions,
        private readonly store: Store,
        private readonly _permPostOfferWorkflowModalManagerService: PermPostOfferWorkflowModalManagerService,
        private readonly _permPostOfferService: PermPostOfferService,
        private readonly _amSubmittalsService: AmSubmittalsService,
        private readonly _accountManagersService: AccountManagersService,
        private readonly _statusUpdateService: AmSubmittalStatusUpdateService,
        private readonly _toasterService: ToasterService,
        private readonly _featureManager: LDFeatureManager
    ) {}
}
